import React, {
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  useRef,
  DetailedHTMLProps,
  useEffect,
} from 'react';
import debounce from 'lodash/debounce';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import defer from 'lodash/defer';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';
import {
  PatientMediaSearchResultDTO,
} from 'dtos';
import clsx from 'clsx';
import {
  useSearchPatientMultimedia,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import head from 'lodash/head';
import CloseIcon from '@mui/icons-material/Close';
import {
  getFileParts,
} from '@populateui/utils';
import {
  formatDate,
} from 'utils/date';
import {
  getFileIcon,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/columns';
import isNil from 'lodash/isNil';
import ToolTip from 'core/components/Tooltip';
import {
  getNoOptionsText,
} from 'core/components/Lookup/Generic';

type RowProps = DetailedHTMLProps<any, any> & {
  media: PatientMediaSearchResultDTO;
};

function MultimediaRow({
  media,
  ...props
}: RowProps) {
  const { fileName, timeStamp, multimediaType } = media;

  const fileParts = getFileParts(fileName ?? '');
  const [fileNamePart, extension] = fileParts;

  return (
    <li
      {...props}
      className={clsx(
        'cursor-pointer p-3 flex items-start gap-3 hover:bg-gray-50 border-b border-b-gray-200 nw-patient-information transition-colors',
        props?.className,
      )}
    >
      <div className="flex-shrink-0">
        {getFileIcon(fileName)}
      </div>
      <div className="flex-1 min-w-0">
        <ToolTip title={fileNamePart ?? ''}>
          <h3 className="font-medium text-xs text-gray-900 truncate">
            {fileNamePart ?? ''}
          </h3>
        </ToolTip>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
          <span className="font-medium">{extension?.toUpperCase() ?? '--'}</span>
          <span>•</span>
          <span>{multimediaType ?? '--'}</span>
          <span>•</span>
          {formatDate(timeStamp ?? '')}
        </div>
      </div>
    </li>
  );
}

type PatientMultimediaLookupProps = {
  patientId: number;
  onSelected: (selected: PatientMediaSearchResultDTO | null) => void;
  onClear?: () => void;
  clearTrigger?: number;
};

export default function PatientMultimediaLookup({
  patientId,
  onSelected,
  onClear,
  clearTrigger,
}: PatientMultimediaLookupProps) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [selected, setSelected] = useState<PatientMediaSearchResultDTO>();
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageChanged, setPageChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!isNil(clearTrigger) && clearTrigger > 0) {
      setSelected(undefined);
    }
  }, [clearTrigger]);

  const fetchNextPage = useCallback(() => {
    setPageChanged(true);
    setPage(page + 1);
  }, [page, setPage]);

  const inputRef = useRef<HTMLInputElement>(null);
  const {
    data: _data,
    isFetching,
    refetch,
  } = useSearchPatientMultimedia(
    patientId,
    searchValue,
    0,
    (page + 1) * 10,
  );

  useEffect(() => {
    if (page > 0) {
      refetch();
    }
  }, [page]);

  const { data: options = [], pageProperties } = _data ?? {};

  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (open) {
      setSearchValue(newInputValue);
    }
  }, 700), [open]);

  const noOptionsText = useMemo(
    () => getNoOptionsText(
      options ?? [],
      searchValue,
      isFetching,
      'Start typing to see matching files',
      'No matching file found',
    ),
    [options, searchValue, isFetching],
  );

  const handleChange = (value: PatientMediaSearchResultDTO | PatientMediaSearchResultDTO[]) => {
    const selected = Array.isArray(value) ? head(value) : value;
    setSelected(selected);
    setOpen(false);

    if (isNil(selected)) {
      onClear?.();
    } else {
      onSelected(selected);
    }
  };

  const dropdownId = `search-patient-multimedia-dropdown-${patientId}`;

  return (
    <AutoCompletedInput<PatientMediaSearchResultDTO>
      id={`search-patient-multimedia-${patientId}`}
      className="patient-multimedia-lookup"
      value={selected ?? ''}
      open={open}
      onOpen={() => { setOpen(true); }}
      clearIcon={isNil(selected) ? null : <CloseIcon className="!w-4 !h-4" />}
      onClose={(event, reason) => {
        defer(() => {
          if (document.activeElement?.closest(`#${dropdownId}`)) {
            return;
          }
          setOpen(false);
        });
      }}
      inputRef={inputRef}
      options={options ?? []}
      getLabel={({ fileName }) => {
        const [fileNamePart] = getFileParts(fileName ?? '');
        return fileNamePart ?? '';
      }}
      onChange={(value) => {
        handleChange(value);
      }}
      onInputChange={handleInputChange}
      popupIcon={isFetching ? <Spinner /> : undefined}
      placeholder="Find media by filename..."
      loading={isFetching}
      loadingText="Searching multimedia..."
      noOptionsText={noOptionsText}
      clearOnBlur={false}
      blurOnSelect={false}
      disableRemove
      openOnFocus
      dropdownClassName="!overflow-x-hidden !p-2"
      slotProps={{
        listbox: {
          component: ListboxDropdown,
          pageProperties: { ...pageProperties },
          fetchNextPage,
          isLoading: isFetching,
          id: dropdownId,
          count: options?.length,
          className: '!max-h-[400px]',
          isLoadingMore: isFetching && pageChanged,
        },
      } as Object}
      renderOption={(props, option) => (
        <MultimediaRow
          {...props}
          media={option}
        />
      )}
      classes={{
        root: '!w-1/3 !min-w-[500px]',
        inputRoot: '!py-2',
      }}
    />
  );
}
